import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

/**
 * The "Locate" pill in an AIS Location column (Maintenance > Vessels, Move Details > Vessels):
 * opens the vessel's AIS location tracker and shows how long ago the vessel was last heard.
 * Show it only to users with "Enable vessel tracking via AIS" (AisAccessService).
 * With iconOnly it is a single map-pin icon to sit inline after a vessel name (Moves list).
 */
@Component({
  selector: 'app-ais-locate-link',
  templateUrl: './ais-locate-link.component.html',
  styleUrls: ['./ais-locate-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AisLocateLinkComponent {
  @Input() vesselId!: number;
  @Input() vesselName?: string;
  @Input() mmsi?: string;
  @Input() reportedAtUtc?: Date;
  /** Open the tracker in a new browser tab/window, keeping the current page as it is. */
  @Input() newWindow = false;
  /** Just the icon, inline after a vessel name; the age and exact time are in the tooltip. */
  @Input() iconOnly = false;

  get trackerLink(): (string | number)[] {
    return ['/maintenance/vessels/ais-location-tracker', this.vesselId];
  }

  get heard(): boolean {
    return !!this.mmsi && !!this.reportedAtUtc;
  }

  /** Compact age of the last known AIS report: "now", "45m", "6h", "3d". */
  get age(): string {
    const minutes = Math.max(0, Math.round((Date.now() - new Date(this.reportedAtUtc!).getTime()) / 60000));
    if (minutes < 1) { return 'now'; }
    if (minutes < 60) { return `${minutes}m`; }
    const hours = Math.floor(minutes / 60);
    return hours < 48 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
  }

  /** Older than a day means all four scheduled checks (5 AM, 10 AM, 5 PM, 10 PM) passed without hearing it. */
  get stale(): boolean {
    return Date.now() - new Date(this.reportedAtUtc!).getTime() > 24 * 60 * 60 * 1000;
  }

  get tooltip(): string {
    const open = this.newWindow ? 'Opens in a new window' : 'Open';
    if (!this.mmsi) {
      return `No MMSI on file yet. ${open} to see how to enable tracking.`;
    }
    if (!this.reportedAtUtc) {
      return `Not heard on AIS yet (MMSI ${this.mmsi}). ${open} to check live.`;
    }
    // Shown in the viewer's own time zone, labelled with it.
    const when = new Date(this.reportedAtUtc).toLocaleString(undefined,
      { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
    return this.iconOnly
      ? `AIS: last known position ${this.age === 'now' ? 'just now' : this.age + ' ago'} (${when}). ${open} to see it on the map.`
      : `Last known position reported ${when}. ${open} to see it on the map.`;
  }

  get ariaLabel(): string {
    return `AIS location for ${this.vesselName ?? 'vessel'}`
      + (this.heard ? `, last reported ${this.age} ago` : '')
      + (this.newWindow ? ' (opens in a new window)' : '');
  }
}
